/**
 * MarketingSocialKeurenPage — social keuren op je telefoon (22-10).
 *
 * Eén voorstel per scherm: beeld op volle breedte, tekst eronder, vier knoppen.
 * Goedkeuren (vaste run 13:00/19:00), Plaats nu (Action start direct), Herschrijf
 * (één zin aanwijzing, de AI schrijft; de aanwijzing is het leersignaal) en
 * Skippen (reden verplicht). Wie het eerst keurt wint; de API bewaakt de race.
 * De volledige pagina (/marketing/social) blijft het toezichtscherm.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSocialStore, type SocialActPayload, type SocialActie, type SocialPost } from '../../stores/social-store';

const PIJLER_LABEL: Record<string, string> = {
  'voor-na': 'voor/na',
  'mooiste-kleed': 'mooiste kleed',
  'achter-de-schermen': 'achter de schermen',
  'klant-aan-het-woord': 'klant aan het woord',
  tips: 'tips',
  ugc: 'ugc',
};

function bronKey(p: SocialPost): string {
  return `${p.wi_sf_id ?? p.bron_ref ?? p.media_ids.join(',')}|${p.format}`;
}

function Media({ post }: { post: SocialPost }) {
  if (post.format === 'carousel' && post.kaarten?.length) {
    return (
      <div className="flex gap-2 overflow-x-auto snap-x px-1">
        {post.kaarten.map((k, i) => (
          <img key={i} src={k.url} alt={k.alt ?? ''} className="snap-center shrink-0 w-[78%] rounded-xl bg-gray-100 object-cover" />
        ))}
      </div>
    );
  }
  if (!post.render_url) return <div className="aspect-[9/16] max-h-[60vh] w-full rounded-xl bg-gray-100" />;
  if (post.format === 'story' || post.format === 'foto') {
    return <img src={post.render_url} alt={post.alt_text ?? ''} className="mx-auto max-h-[60vh] rounded-xl bg-gray-100 object-contain" />;
  }
  return (
    <video
      key={post.id}
      src={post.render_url}
      poster={post.cover_url ?? undefined}
      controls
      playsInline
      preload="metadata"
      className="mx-auto max-h-[60vh] w-full rounded-xl bg-black object-contain"
    />
  );
}

export function MarketingSocialKeurenPage() {
  const { posts, isLoading, error, fetchPosts, act } = useSocialStore();
  const [i, setI] = useState(0);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [reden, setReden] = useState('');
  const [aanwijzing, setAanwijzing] = useState('');
  const [paneel, setPaneel] = useState<'geen' | 'skip' | 'herschrijf'>('geen');
  const [busy, setBusy] = useState<SocialActie | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts('gerenderd', null);
  }, [fetchPosts]);

  // Facebook-zusters delen render en tekst en volgen het besluit: één kaart per bron.
  const lijst = useMemo(() => {
    const ig = posts.filter((p) => p.platform === 'instagram');
    const igKeys = new Set(ig.map(bronKey));
    return [...ig, ...posts.filter((p) => p.platform !== 'instagram' && !igKeys.has(bronKey(p)))].sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [posts]);
  const post = lijst[Math.min(i, Math.max(0, lijst.length - 1))];
  const zusters = post ? posts.filter((q) => q.id !== post.id && bronKey(q) === bronKey(post)).map((q) => q.platform) : [];

  useEffect(() => {
    setCaption(post?.caption ?? '');
    setHashtags((post?.hashtags ?? []).join(' '));
    setReden('');
    setAanwijzing('');
    setPaneel('geen');
    setFout(null);
  }, [post?.id, post?.caption, post?.hashtags]);

  const gewijzigd = !!post && (caption !== (post.caption ?? '') || hashtags !== (post.hashtags ?? []).join(' '));

  async function doe(a: SocialActie, payload?: SocialActPayload) {
    if (!post) return;
    setBusy(a);
    setFout(null);
    setMelding(null);
    try {
      if (gewijzigd && (a === 'approve' || a === 'publish_now')) {
        await act(post.id, 'caption', { caption, hashtags: hashtags.split(/\s+/).map((h) => h.replace(/^#/, '')).filter(Boolean) });
      }
      const uit = await act(post.id, a, payload);
      if (a === 'rewrite') {
        setMelding('Herschreven. Lees hem na en keur goed, of geef nog een aanwijzing.');
        setPaneel('geen');
        setAanwijzing('');
      } else {
        setMelding(uit.melding ?? (a === 'approve' ? 'Goedgekeurd; gaat mee in de run van 13:00 of 19:00.' : a === 'skip' ? 'Geskipt.' : 'Gedaan.'));
      }
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Mislukt');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-md px-4 pb-24 pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Social keuren</h1>
            <p className="text-xs text-gray-500">{lijst.length > 0 ? `${Math.min(i + 1, lijst.length)} van ${lijst.length} wacht${lijst.length === 1 ? '' : 'en'}` : 'niets te keuren'}</p>
          </div>
          <Link to="/marketing/social" className="text-xs text-indigo-600 hover:underline">
            Volledige pagina
          </Link>
        </div>

        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {melding && <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{melding}</div>}

        {isLoading && !post && <p className="text-sm text-gray-400">Laden…</p>}
        {!isLoading && !post && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
            Alles is beoordeeld. De volgende voorstellen komen morgenochtend rond 08:30.
          </div>
        )}

        {post && (
          <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="rounded-full bg-teal-50 px-2 py-0.5 text-teal-700">{PIJLER_LABEL[post.pijler] ?? post.pijler}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{post.format}</span>
              {zusters.map((z) => (
                <span key={z} className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">+ {z}</span>
              ))}
              {post.kleed_soort && <span className="text-gray-500">{post.kleed_soort} {post.kleed_afmeting ?? ''}</span>}
              <span className="ml-auto text-gray-400">{new Date(post.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}</span>
            </div>

            <Media post={post} />

            {post.format !== 'story' && (
              <>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={7}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[15px] leading-snug"
                />
                <input
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600"
                  placeholder="hashtags gescheiden door spaties"
                />
                <p className="text-[11px] text-gray-400">
                  {caption.length} tekens{post.caption_bewerkt_door ? ` · bewerkt door ${post.caption_bewerkt_door}` : ''}
                  {gewijzigd ? ' · wijziging gaat mee bij goedkeuren' : ''}
                </p>
              </>
            )}

            {fout && <p className="text-sm text-red-600">{fout}</p>}

            {paneel === 'herschrijf' && (
              <div className="space-y-2 rounded-lg bg-indigo-50 p-3">
                <p className="text-xs text-indigo-900">Wat moet anders? Eén zin is genoeg. Dit leert de schrijver ook voor de volgende keer.</p>
                <input
                  value={aanwijzing}
                  onChange={(e) => setAanwijzing(e.target.value)}
                  autoFocus
                  placeholder="bijv. korter, minder over schuim, zeg dat dit Joost is"
                  className="w-full rounded-lg border border-indigo-200 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => doe('rewrite', { aanwijzing })}
                    disabled={busy !== null || aanwijzing.trim().length < 3}
                    className="rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-40"
                  >
                    {busy === 'rewrite' ? 'Schrijft…' : 'Herschrijf'}
                  </button>
                  <button onClick={() => setPaneel('geen')} className="px-3 py-2 text-sm text-gray-500">
                    annuleer
                  </button>
                </div>
              </div>
            )}

            {paneel === 'skip' && (
              <div className="space-y-2 rounded-lg bg-gray-100 p-3">
                <input
                  value={reden}
                  onChange={(e) => setReden(e.target.value)}
                  autoFocus
                  placeholder="Waarom niet? (verplicht)"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button onClick={() => doe('skip', { reden })} disabled={busy !== null || reden.trim().length < 3} className="rounded-lg bg-gray-800 px-3 py-2 text-sm text-white disabled:opacity-40">
                    Bevestig skip
                  </button>
                  <button onClick={() => setPaneel('geen')} className="px-3 py-2 text-sm text-gray-500">
                    annuleer
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1">
              <button onClick={() => doe('approve')} disabled={busy !== null} className="rounded-lg bg-indigo-600 px-3 py-3 text-sm font-medium text-white disabled:opacity-50">
                {busy === 'approve' ? 'Bezig…' : 'Goedkeuren'}
              </button>
              <button onClick={() => doe('publish_now')} disabled={busy !== null} className="rounded-lg border border-indigo-600 px-3 py-3 text-sm font-medium text-indigo-700 disabled:opacity-50">
                {busy === 'publish_now' ? 'Bezig…' : 'Plaats nu'}
              </button>
              {post.format !== 'story' && (
                <button onClick={() => setPaneel(paneel === 'herschrijf' ? 'geen' : 'herschrijf')} disabled={busy !== null} className="rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700 disabled:opacity-50">
                  Herschrijf
                </button>
              )}
              <button onClick={() => setPaneel(paneel === 'skip' ? 'geen' : 'skip')} disabled={busy !== null} className="rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700 disabled:opacity-50">
                Skippen
              </button>
            </div>
          </div>
        )}

        {lijst.length > 1 && (
          <div className="flex items-center justify-between text-sm">
            <button onClick={() => setI((x) => Math.max(0, x - 1))} disabled={i <= 0} className="px-3 py-2 text-gray-600 disabled:opacity-30">
              vorige
            </button>
            <button onClick={() => setI((x) => Math.min(lijst.length - 1, x + 1))} disabled={i >= lijst.length - 1} className="px-3 py-2 text-gray-600 disabled:opacity-30">
              volgende
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
